/* ============================================================================
   bygge DIY-Integration · Bootstrap
   ----------------------------------------------------------------------------
   Lädt initial mit der PDP, ist aber leichtgewichtig (~3 KB).
   Lauscht auf Variant-Wechsel und lädt den schweren Konfigurator
   erst on-demand beim ersten Klick auf die DIY-Variante.

   ÄNDERUNG (Loader-Fix):
     - Beim DIY-Klick wird window.__byggeLoaderStart gesetzt, damit die
       Loader-Mindestsichtzeit AB KLICK gemessen wird (nicht erst ab init()).
       So ist die Lade-Animation auch bei warmem Cache zuverlässig sichtbar.
   ========================================================================= */
(function () {
  'use strict';

  const cfg = window.byggeDiyConfig;
  if (!cfg) return;

  const SECTION_ID = cfg.sectionId;
  let heavyAssetsLoaded = false;
  let configuratorInstance = null;
  let isDiyActive = false;

  // Loader-Markup zentral, damit integration + configurator identisch aussehen
  const LOADER_HTML = `
    <div class="bygge-config-loading" data-loading>
      <div class="bygge-config-loader-bricks">
        <span></span><span></span><span></span><span></span>
      </div>
      <div class="bygge-config-loader-text">Konfigurator wird geladen</div>
    </div>
  `;

  // === DOM-Referenzen ===
  function getDomRefs() {
    return {
      mediaWrapper: document.querySelector(`[data-section="${SECTION_ID}"] .bygge-media-wrapper`),
      configHost: document.getElementById(`ByggeConfigHost-${SECTION_ID}`),
      mediaGallery: document.getElementById(`MediaGallery-${SECTION_ID}`),
      atcForm: document.getElementById(`product-form-${SECTION_ID}`),
      atcButton: document.querySelector(`#product-form-${SECTION_ID} button[name="add"]`),
      diyRadio: document.querySelector(`[data-bygge-diy-radio]`),
      allVariantRadios: document.querySelectorAll(`variant-radios[data-section="${SECTION_ID}"] input[type="radio"]`),
      priceBox: document.getElementById(`bygge-price-${SECTION_ID}`),
      legacyPrice: document.getElementById(`price-${SECTION_ID}`),
      roomCta: document.querySelector('[data-bygge-room-cta]'),
    };
  }

  // === DIY-Preis (in Cents wie Shopify es nutzt) ===
  const DIY_PRICE_CENTS = 16900; // 169.00 €
  const DIY_PRICE_LABEL = '169,00 €';

  // Original-Werte sichern, um beim Wechsel zurück wiederherstellen zu können
  let originalState = {
    saved: false,
    priceBoxHTML: null,
    legacyPriceHTML: null,
    atcButtonHTML: null,
    atcButtonDisabled: null,
    atcButtonAriaDisabled: null,
  };

  function saveOriginalState() {
    if (originalState.saved) return;
    const refs = getDomRefs();
    if (refs.priceBox) originalState.priceBoxHTML = refs.priceBox.innerHTML;
    if (refs.legacyPrice) originalState.legacyPriceHTML = refs.legacyPrice.innerHTML;
    if (refs.atcButton) {
      originalState.atcButtonHTML = refs.atcButton.innerHTML;
      originalState.atcButtonDisabled = refs.atcButton.disabled;
      originalState.atcButtonAriaDisabled = refs.atcButton.getAttribute('aria-disabled');
    }
    originalState.saved = true;
  }

  function setDiyPriceAndButton() {
    const refs = getDomRefs();

    // Preis-Box (bygge-Block)
    if (refs.priceBox) {
      refs.priceBox.innerHTML = `
        <div class="price price--large bygge-price">
          <div class="price__container">
            <div class="price__regular">
              <span class="price-item price-item--regular">${DIY_PRICE_LABEL}</span>
            </div>
          </div>
        </div>
      `;
    }

    // Legacy-Price-Block falls vorhanden (versteckt durch CSS, aber sicherheitshalber)
    if (refs.legacyPrice) {
      refs.legacyPrice.innerHTML = `
        <div class="price price--large">
          <div class="price__regular">
            <span class="price-item price-item--regular">${DIY_PRICE_LABEL}</span>
          </div>
        </div>
      `;
    }

    // ATC-Button: aktivieren + Text + Spinner-Markup behalten
    if (refs.atcButton) {
      refs.atcButton.disabled = false;
      refs.atcButton.removeAttribute('aria-disabled');
      refs.atcButton.removeAttribute('disabled');
      const span = refs.atcButton.querySelector('span:not(.visually-hidden)');
      if (span) {
        span.textContent = 'Mein DIY-Set in den Warenkorb';
      }
    }
  }

  function restoreOriginalState() {
    const refs = getDomRefs();
    if (!originalState.saved) return;

    if (refs.priceBox && originalState.priceBoxHTML !== null) {
      refs.priceBox.innerHTML = originalState.priceBoxHTML;
    }
    if (refs.legacyPrice && originalState.legacyPriceHTML !== null) {
      refs.legacyPrice.innerHTML = originalState.legacyPriceHTML;
    }
    if (refs.atcButton && originalState.atcButtonHTML !== null) {
      refs.atcButton.innerHTML = originalState.atcButtonHTML;
      refs.atcButton.disabled = originalState.atcButtonDisabled;
      if (originalState.atcButtonAriaDisabled === null) {
        refs.atcButton.removeAttribute('aria-disabled');
      } else {
        refs.atcButton.setAttribute('aria-disabled', originalState.atcButtonAriaDisabled);
      }
    }
  }

  // === Script Loader ===
  // Race-Condition-sicher: wenn dasselbe Script währenddessen lädt (z.B. weil
  // Prefetch und Klick gleichzeitig triggern), warten wir auf dieselbe Promise.
  const scriptPromises = new Map();

  function loadScript(src) {
    // Schon eine laufende oder fertige Promise für dieses Script? → wiederverwenden
    if (scriptPromises.has(src)) {
      return scriptPromises.get(src);
    }

    const p = new Promise((resolve, reject) => {
      // Falls Script-Tag schon im DOM (z.B. von externem Code), trotzdem onload abwarten
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        if (existing.dataset.byggeLoaded === '1') {
          resolve();
          return;
        }
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('Failed to load: ' + src)), { once: true });
        return;
      }

      const s = document.createElement('script');
      s.src = src;
      s.async = false;
      s.onload = () => {
        s.dataset.byggeLoaded = '1';
        resolve();
      };
      s.onerror = () => {
        scriptPromises.delete(src);  // bei Fehler: nochmal probierbar machen
        reject(new Error('Failed to load: ' + src));
      };
      document.head.appendChild(s);
    });

    scriptPromises.set(src, p);
    return p;
  }

  // === Heavy Assets nachladen ===
  // Alle Three.js-Scripts von einer CDN (jsdelivr) — spart DNS-Lookups.
  // WICHTIG: THREE muss VOR den Plugins (GLTFLoader/OrbitControls) verfügbar sein,
  // sonst werfen die Plugin-IIFEs still und THREE.GLTFLoader bleibt undefined.
  async function loadHeavyAssets() {
    if (heavyAssetsLoaded) return;

    try {
      // GLBs SOFORT parallel anstoßen — sie hängen nicht von THREE ab
      const glbPrefetches = prefetchGLBs(cfg.modelUrls);

      // Three.js Core SEQUENZIELL zuerst — Plugins brauchen das globale THREE-Objekt
      await loadScript('https://cdn.jsdelivr.net/npm/three@0.128.0/build/three.min.js');

      // Plugins + Configurator parallel (THREE ist jetzt verfügbar)
      await Promise.all([
        loadScript('https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/GLTFLoader.js'),
        loadScript('https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/controls/OrbitControls.js'),
        loadScript(cfg.scriptUrl),
      ]);

      // GLB-Prefetch sollte jetzt eh fertig sein
      await glbPrefetches;

      heavyAssetsLoaded = true;
    } catch (err) {
      console.error('[bygge-diy] Asset-Loading fehlgeschlagen:', err);
      throw err;
    }
  }

  // GLBs parallel als ArrayBuffer holen → in window.__byggeGlbCache speichern.
  // Der Configurator schaut beim loadTemplate zuerst dort nach.
  async function prefetchGLBs(modelUrls) {
    if (!modelUrls) return;
    window.__byggeGlbCache = window.__byggeGlbCache || {};
    const cache = window.__byggeGlbCache;
    const promises = Object.entries(modelUrls).map(async ([key, url]) => {
      if (cache[url]) return;
      try {
        const res = await fetch(url, { mode: 'cors' });
        if (!res.ok) return;
        cache[url] = await res.arrayBuffer();
      } catch (e) {
        // Stiller Fehler — der GLTFLoader wird die URL eh nochmal selbst laden
        console.warn('[bygge-diy-integration] GLB-Prefetch fehlgeschlagen:', url, e.message);
      }
    });
    return Promise.all(promises);
  }

  // === Smooth-Scroll zum Konfigurator ===
  function scrollToConfigurator() {
    const refs = getDomRefs();
    if (!refs.mediaWrapper) return;

    const rect = refs.mediaWrapper.getBoundingClientRect();
    const isMobile = window.innerWidth <= 749;
    // Sticky Header berücksichtigen: ~80px auf Desktop, ~60px Mobile
    const headerOffset = isMobile ? 60 : 80;
    const targetY = window.scrollY + rect.top - headerOffset;

    window.scrollTo({
      top: targetY,
      behavior: 'smooth',
    });
  }

  // === Host-Größe an Media-Wrapper angleichen ===
  // Da bei DIY-Modus die Gallery height=0 hat, messen wir den Wrapper selbst
  // (der durch aspect-ratio CSS seine Größe behält)
  function syncHostSize() {
    const refs = getDomRefs();
    if (!refs.configHost || !refs.mediaWrapper) return;

    const wrapperRect = refs.mediaWrapper.getBoundingClientRect();
    if (!wrapperRect.width || !wrapperRect.height) return;

    refs.configHost.style.top = '0px';
    refs.configHost.style.left = '0px';
    refs.configHost.style.width = wrapperRect.width + 'px';
    refs.configHost.style.height = wrapperRect.height + 'px';
  }

  // === DIY-Modus aktivieren ===
  async function activateDiy() {
    const refs = getDomRefs();
    if (!refs.configHost) return;

    isDiyActive = true;

    // Wichtig: V5-Preis-Sync blockieren, damit unsere DIY-Preis-Anzeige nicht überschrieben wird
    window.byggeSkipPriceSync = true;

    // Original-State sichern (nur beim ersten Mal)
    saveOriginalState();

    // ZUERST die diy-active Klasse setzen, damit der Wrapper seine aspect-ratio bekommt
    refs.mediaWrapper.classList.add('bygge-diy-active');

    // Host sichtbar machen
    refs.configHost.removeAttribute('hidden');
    refs.configHost.setAttribute('aria-hidden', 'false');

    // Layout-Reflow forcieren, damit aspect-ratio greift
    void refs.mediaWrapper.offsetHeight;

    // JETZT erst die Host-Größe an den (nun gestylten) Wrapper angleichen
    requestAnimationFrame(() => {
      syncHostSize();
      setTimeout(() => syncHostSize(), 50);
    });

    // Smooth-Scroll zum Canvas
    scrollToConfigurator();

    // Resize-Handler einmalig binden
    if (!window._byggeResizeBound) {
      window._byggeResizeBound = true;
      window.addEventListener('resize', () => {
        if (isDiyActive) syncHostSize();
      });
    }

    // Preis + Button auf DIY-State setzen.
    // Dawn's variant-radios führt das Update SYNCHRON nach dem Click aus.
    // Wir müssen NACH Dawn agieren — daher mehrfach gestaffelt.
    setDiyPriceAndButton();
    requestAnimationFrame(() => setDiyPriceAndButton());
    setTimeout(() => setDiyPriceAndButton(), 100);
    setTimeout(() => setDiyPriceAndButton(), 300);

    // Loader-Logik: nur anzeigen wenn wir wirklich initialisieren (1. Klick).
    // Bei wiederholtem Klick ist configuratorInstance schon da → kein Loader nötig.
    const willInit = !configuratorInstance && refs.configHost;
    if (willInit && !refs.configHost.querySelector('.bygge-config-loading')) {
      // LOADER-FIX: Startzeit AB JETZT (= ab Klick) festhalten, damit die
      // Mindest-Sichtzeit im Configurator korrekt ab dem Klick gemessen wird
      // und der Loader auch bei warmem Cache zuverlässig sichtbar bleibt.
      window.__byggeLoaderStart = performance.now();
      refs.configHost.innerHTML = LOADER_HTML;
    } else if (!willInit && refs.configHost) {
      // Sicherheits-Cleanup: falls aus irgendeinem Grund noch ein Loader rumhängt
      const stale = refs.configHost.querySelector('.bygge-config-loading');
      if (stale) stale.remove();
    }

    // Heavy Assets laden falls noch nicht da
    console.log('[bygge-diy-integration] Lade heavy assets...');
    try {
      await loadHeavyAssets();
      console.log('[bygge-diy-integration] Heavy assets geladen, byggeConfigurator:', !!window.byggeConfigurator);
    } catch (err) {
      console.error('[bygge-diy-integration] Asset-Load fehlgeschlagen:', err);
      refs.configHost.innerHTML = '<div class="bygge-config-error">Konfigurator konnte nicht geladen werden.<br>Bitte Seite neu laden.</div>';
      return;
    }

    // Konfigurator instanziieren (nur beim ersten Mal)
    if (!configuratorInstance && window.byggeConfigurator) {
      console.log('[bygge-diy-integration] Rufe byggeConfigurator.init auf...');
      configuratorInstance = window.byggeConfigurator.init(refs.configHost, {
        modelUrls: cfg.modelUrls,
      });
      requestAnimationFrame(syncHostSize);
    } else {
      console.log('[bygge-diy-integration] Init übersprungen - instance:', !!configuratorInstance, 'configurator:', !!window.byggeConfigurator);
    }
  }

  // === DIY-Modus deaktivieren ===
  function deactivateDiy() {
    const refs = getDomRefs();
    if (!refs.configHost) return;

    isDiyActive = false;
    refs.configHost.setAttribute('hidden', '');
    refs.configHost.setAttribute('aria-hidden', 'true');
    refs.mediaWrapper.classList.remove('bygge-diy-active');

    // V5-Preis-Sync wieder erlauben — SOFORT synchron,
    // damit der parallel laufende V5-change-Listener nicht blockiert wird
    window.byggeSkipPriceSync = false;

    // Falls V5 noch nicht gefeuert hat (Race-Condition-Schutz), manuell triggern
    if (typeof window.byggeTriggerPriceSync === 'function') {
      window.byggeTriggerPriceSync();
    }
  }

  // === Cart Submit Hijack ===
  function setupCartHijack() {
    const refs = getDomRefs();
    if (!refs.atcForm) return;

    refs.atcForm.addEventListener('submit', function (e) {
      if (!isDiyActive) return;  // Normalmodus: Standard-Submit
      if (!configuratorInstance || !window.byggeConfigurator) return;

      e.preventDefault();
      e.stopPropagation();

      const placedBlocks = window.byggeConfigurator.getConfiguration();
      if (!placedBlocks || placedBlocks.length !== 12) {
        showCartError('Bitte warte, bis alle 12 Module geladen sind.');
        return;
      }

      const cartItems = [];
      for (const block of placedBlocks) {
        const typeKey = block.type.toUpperCase();
        const color = block.currentColor.toUpperCase();
        const variantId = cfg.productVariants[typeKey] && cfg.productVariants[typeKey][color];
        if (!variantId) {
          console.warn('[bygge-diy] Keine Variant-ID für', typeKey, color);
          continue;
        }
        cartItems.push({ id: variantId, quantity: 1 });
      }

      if (cartItems.length !== 12) {
        showCartError(`Es wurden nur ${cartItems.length} von 12 Modulen erkannt. Bitte prüfe die Farben.`);
        return;
      }

      // Button-State während Submit
      setButtonLoading(refs.atcButton, true);

      // Conversion-Tracking: feuern BEVOR der API-Call läuft.
      // Wir vergleichen die gekauften Farben mit der letzten KI-Empfehlung
      // → so können wir messen, wie oft jemand nach KI-Empfehlung kauft.
      const purchasedColors = Array.from(new Set(placedBlocks.map((b) => b.currentColor.toUpperCase())));
      const recommendation = (typeof window.byggeGetRecommendation === 'function')
        ? window.byggeGetRecommendation()
        : null;

      let aiState = 'no_ai';
      let aiMatch = null;
      if (recommendation && Array.isArray(recommendation.color_hexes)) {
        const recHexes = recommendation.color_hexes.map((h) => h.toUpperCase());
        const intersection = purchasedColors.filter((h) => recHexes.includes(h));
        const isExactMatch = intersection.length === recHexes.length && purchasedColors.length === recHexes.length;
        const isPartialMatch = intersection.length > 0;

        if (recommendation.applied && isExactMatch) {
          aiState = 'ai_applied_exact';
        } else if (recommendation.applied && isPartialMatch) {
          aiState = 'ai_applied_modified';
        } else if (recommendation.applied) {
          aiState = 'ai_applied_then_changed';
        } else if (isPartialMatch) {
          aiState = 'ai_shown_partial_used';
        } else {
          aiState = 'ai_shown_unused';
        }

        aiMatch = {
          recommendation_id: recommendation.recommendation_id,
          recommended_colors: recommendation.colors,
          recommended_hexes: recHexes,
          applied: !!recommendation.applied,
          exact_match: isExactMatch,
          partial_match: isPartialMatch,
          overlap_count: intersection.length,
        };
      }

      if (typeof window.byggeTrack === 'function') {
        window.byggeTrack('bygge_diy_add_to_cart', {
          ai_state: aiState,
          purchased_color_count: purchasedColors.length,
          purchased_hexes: purchasedColors,
          ai_recommendation: aiMatch,
          cart_value_cents: cartItems.length * 1400,  // ungefähr; je nach Stein-Mix etwas anders
        });
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: cartItems }),
      })
        .then((r) => {
          if (!r.ok) throw new Error('Cart-API ' + r.status);
          return r.json();
        })
        .then(() => {
          // Versuche, das Cart-Drawer-System des Themes zu nutzen
          // Fallback: redirect zu /cart
          if (typeof window.routes !== 'undefined' && document.querySelector('cart-notification')) {
            // Dawn-Cart-Notification triggern wäre möglich, aber komplex
            window.location.href = '/cart';
          } else {
            window.location.href = '/cart';
          }
        })
        .catch((err) => {
          console.error(err);
          showCartError('Fehler beim Hinzufügen. Bitte erneut versuchen.');
          setButtonLoading(refs.atcButton, false);
        });
    }, true); // capture phase, damit wir vor Dawn's Product-Form-Handler dran sind
  }

  function setButtonLoading(button, isLoading) {
    if (!button) return;
    if (isLoading) {
      button.classList.add('loading');
      const spinner = button.querySelector('.loading-overlay__spinner');
      if (spinner) spinner.classList.remove('hidden');
      button.setAttribute('disabled', '');
    } else {
      button.classList.remove('loading');
      const spinner = button.querySelector('.loading-overlay__spinner');
      if (spinner) spinner.classList.add('hidden');
      button.removeAttribute('disabled');
    }
  }

  function showCartError(msg) {
    const refs = getDomRefs();
    const form = refs.atcForm;
    if (!form) {
      alert(msg);
      return;
    }
    const errorWrap = form.querySelector('.product-form__error-message-wrapper');
    const errorText = form.querySelector('.product-form__error-message');
    if (errorWrap && errorText) {
      errorText.textContent = msg;
      errorWrap.removeAttribute('hidden');
      setTimeout(() => errorWrap.setAttribute('hidden', ''), 5000);
    } else {
      alert(msg);
    }
  }

  // === Variant-Wechsel Detection ===
  // ROBUSTER ANSATZ: Event-Delegation auf document (capture phase),
  // weil Dawn's variant-radios Web-Component die Radios bei Sync teilweise neu rendert.
  // So bekommen wir Klicks auf das DIY-Radio auch nach DOM-Updates mit.
  function setupVariantWatcher() {
    console.log('[bygge-diy-integration] setupVariantWatcher: registriere Delegation-Listener');

    // PREFETCH: Bei Hover (Desktop) oder Touch-Start (Mobile) auf den DIY-Radio
    // ODER den Room-CTA-Button → schon die Assets vorladen.
    let prefetchTriggered = false;
    document.addEventListener('mouseover', maybePrefetchAssets, true);
    document.addEventListener('touchstart', maybePrefetchAssets, { capture: true, passive: true });

    function maybePrefetchAssets(e) {
      if (prefetchTriggered) return;
      const target = e.target;
      if (!target || !target.closest) return;
      // Hover/Touch auf DIY-Radio, sein Label, ODER den Room-CTA-Button?
      const onDiy = target.closest('[data-bygge-diy-radio]') ||
        target.closest('[data-bygge-room-cta]') ||
        (target.tagName === 'LABEL' && target.getAttribute('for') &&
         document.getElementById(target.getAttribute('for'))?.hasAttribute('data-bygge-diy-radio')
          ? target : null);
      if (!onDiy) return;
      prefetchTriggered = true;
      console.log('[bygge-diy-integration] Prefetch triggered (hover/touch on DIY or Room-CTA)');
      loadHeavyAssets().catch(() => { prefetchTriggered = false; });
    }

    // Room-CTA Klick-Handler: DIY aktivieren + File-Picker öffnen
    document.addEventListener('click', handleRoomCtaClick);

    // Sowohl 'change' als auch 'click' abfangen — change feuert bei Tastatur-Auswahl,
    // click feuert auch wenn das Radio bereits checked ist (selten, aber zur Sicherheit).
    document.addEventListener('change', handleRadioChange, true);  // capture phase
    document.addEventListener('click', handleRadioClick, true);

    // Initial-Check: falls DIY schon vorausgewählt
    const refs = getDomRefs();
    if (refs.diyRadio && refs.diyRadio.checked) {
      console.log('[bygge-diy-integration] DIY beim Boot vorausgewählt, aktiviere...');
      activateDiy();
    }
  }

  function handleRadioChange(e) {
    const target = e.target;
    if (!target || target.type !== 'radio') return;
    // Nur Radios in unserer Variant-Picker-Section beachten
    if (!target.closest(`variant-radios[data-section="${SECTION_ID}"]`)) return;
    console.log('[bygge-diy-integration] Radio change:', target.value, 'isDiy:', target.hasAttribute('data-bygge-diy-radio'));
    routeRadioSelection(target);
  }

  function handleRadioClick(e) {
    const target = e.target;
    // Klick kann auf input ODER auf das wrapping label kommen
    let radio = null;
    if (target.type === 'radio') {
      radio = target;
    } else if (target.tagName === 'LABEL' || target.closest('label')) {
      const label = target.tagName === 'LABEL' ? target : target.closest('label');
      const forId = label.getAttribute('for');
      if (forId) radio = document.getElementById(forId);
    }
    if (!radio || radio.type !== 'radio') return;
    if (!radio.closest(`variant-radios[data-section="${SECTION_ID}"]`)) return;
    // Klick auf bereits-checked Radio → erneut routen (z.B. zurück auf DIY nach Standard)
    if (radio.checked) {
      console.log('[bygge-diy-integration] Radio click (already checked):', radio.value);
      routeRadioSelection(radio);
    }
  }

  function routeRadioSelection(radio) {
    if (radio.hasAttribute('data-bygge-diy-radio')) {
      console.log('[bygge-diy-integration] → DIY aktivieren');
      activateDiy();
    } else {
      if (isDiyActive) {
        console.log('[bygge-diy-integration] → DIY deaktivieren');
        deactivateDiy();
      }
    }
  }

  // Klick auf den Room-CTA-Button ODER das DIY-Variant-Label:
  // → DIY aktivieren + File-Picker triggern.
  // Funktioniert auch wenn DIY schon aktiv ist (dann nur File-Picker).
  async function handleRoomCtaClick(e) {
    const target = e.target;
    if (!target || !target.closest) return;
    const ctaButton = target.closest('[data-bygge-room-cta]');
    if (!ctaButton) return;

    // Tracking: Hero-CTA wurde geklickt
    if (typeof window.byggeTrack === 'function') {
      window.byggeTrack('bygge_ai_cta_clicked', { source: 'hero_pill' });
    } else {
      // Fallback (Tracker noch nicht geladen) → dataLayer direkt
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'bygge_ai_cta_clicked',
          source: 'hero_pill',
          timestamp: new Date().toISOString(),
        });
      } catch (err) {}
    }

    // Unterscheidung: ist es ein <label for="...">  → Radio-Logik vom Browser nutzen
    //                 oder ein echter <button>      → manuell activate
    const isLabelForRadio = ctaButton.tagName === 'LABEL' && ctaButton.getAttribute('for');

    if (isLabelForRadio) {
      // Browser handhabt Radio-Check über label[for] automatisch.
      // activateDiy startet via die normalen change-Listener.
      console.log('[bygge-diy-integration] DIY-Label geklickt → File-Picker nach Boot');
    } else {
      // Echter Button (z.B. der Banner unter dem Hero) — manuelle Aktivierung
      e.preventDefault();
      console.log('[bygge-diy-integration] Room-CTA Button geklickt');
      const refsForActivate = getDomRefs();
      if (refsForActivate.diyRadio && !refsForActivate.diyRadio.checked) {
        refsForActivate.diyRadio.checked = true;
        refsForActivate.diyRadio.dispatchEvent(new Event('change', { bubbles: true }));
        await activateDiy();
      } else if (!isDiyActive) {
        await activateDiy();
      }
    }

    // Warten bis Configurator geladen ist (Polling, max 8s)
    const start = Date.now();
    while (!configuratorInstance && Date.now() - start < 8000) {
      await new Promise((r) => setTimeout(r, 100));
    }

    // CTA-Button im Konfigurator triggern (öffnet Intro-Modal, der dann File-Picker startet)
    // → konsistent: egal ob Hero-Pille oder Canvas-Button, beide gehen über das Intro-Modal
    const refs = getDomRefs();
    const aiBtn = refs.configHost && refs.configHost.querySelector('[data-ai-btn]');
    if (aiBtn) {
      console.log('[bygge-diy-integration] Triggere CTA-Button → öffnet Intro-Modal');
      // Marker setzen, damit der CTA-Handler kein doppeltes Tracking-Event feuert
      aiBtn.dataset.byggeSyntheticClick = '1';
      aiBtn.click();
      // Marker direkt wieder entfernen
      setTimeout(() => { delete aiBtn.dataset.byggeSyntheticClick; }, 50);
    } else {
      // Fallback falls aiBtn nicht gefunden (alter Configurator?) → direkt File-Picker
      const fileInput = refs.configHost && refs.configHost.querySelector('.bygge-config-ai-file');
      if (fileInput) {
        console.log('[bygge-diy-integration] Fallback: triggere File-Picker direkt');
        fileInput.click();
      } else {
        console.warn('[bygge-diy-integration] File-Picker nicht gefunden — Configurator vielleicht noch nicht bereit');
      }
    }
  }

  // === Boot ===
  function boot() {
    setupVariantWatcher();
    setupCartHijack();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();